import { useState } from 'react'
import type { StepView, WireOutcome } from '../../data/repository'
import { ScanField } from '../ScanField'

/**
 * One step: the instruction, large, and the one control that answers it.
 *
 * Plain words for every kind — "Pass / Fail / Doesn't apply", "Scan the
 * label", "Use 14 from the shelf" — rather than the engine's own names for
 * them, which used to sit on every step as a badge.
 */
export function StepCard({
  step,
  total,
  busy,
  onAnswer,
}: {
  step: StepView
  total: number
  busy: boolean
  onAnswer: (outcome: WireOutcome, note: string) => void
}): JSX.Element {
  const [note, setNote] = useState(step.answer?.note ?? '')
  const [serial, setSerial] = useState(step.answer?.childSerial ?? '')
  const [value, setValue] = useState(step.answer?.value?.toString() ?? '')
  const [amount, setAmount] = useState((step.answer?.amount ?? step.amount ?? 1).toString())

  return (
    <div className="panel panel--padded step-card">
      <span className="step-card__count">
        Step {step.ordinal} of {total}
      </span>
      <h2 className="step-card__text">{step.text}</h2>
      {step.position ? (
        <p className="step-card__detail">
          Into <strong>{step.position.replace(/_/g, ' ')}</strong>
          {step.expectedTypeName ? <> · a {step.expectedTypeName}</> : null}
          {step.fittedSerial ? (
            <>
              {' '}· fitted now <span className="serial">{step.fittedSerial}</span>
            </>
          ) : null}
        </p>
      ) : null}

      {step.kind === 'check' ? (
        <>
          <div className="step-card__choices">
            <button className="primary big-action" disabled={busy} onClick={() => onAnswer({ kind: 'check', result: 'pass' }, note)}>
              Pass
            </button>
            <button className="big-action" disabled={busy} onClick={() => onAnswer({ kind: 'check', result: 'fail' }, note)}>
              Fail
            </button>
            {/* n.a. is an answer: a step nobody answered and a step somebody
                decided did not apply look identical afterwards otherwise. */}
            <button className="big-action" disabled={busy} onClick={() => onAnswer({ kind: 'check', result: 'na' }, note)}>
              Doesn’t apply
            </button>
          </div>
          <textarea placeholder="Note — say what you saw if it fails" value={note} onChange={(ev) => setNote(ev.target.value)} rows={2} />
        </>
      ) : null}

      {step.kind === 'measure' ? (
        <>
          <label className="field">
            <span className="field-label">
              {step.measurementType?.replace(/_/g, ' ')} ({step.unit})
              {step.lower !== null || step.upper !== null ? ` · expected ${step.lower ?? '−∞'} to ${step.upper ?? '∞'}` : ''}
            </span>
            <input type="number" inputMode="decimal" step="any" className="big-input" value={value} onChange={(ev) => setValue(ev.target.value)} />
          </label>
          {value !== '' && band(step, Number(value)) === false ? (
            <p className="warn" style={{ margin: 0 }}>
              Outside what’s expected. Record it anyway if that’s the reading — the trend is the point.
            </p>
          ) : null}
          <button className="primary big-action" disabled={busy || value === ''} onClick={() => onAnswer({ kind: 'measure', value: Number(value), unit: step.unit ?? '' }, note)}>
            Record
          </button>
        </>
      ) : null}

      {step.kind === 'install' ? (
        <ScanField
          value={serial}
          onChange={setSerial}
          busy={busy}
          label="Or type the serial of the part going in"
          onSerial={(s) => onAnswer({ kind: 'install', childSerial: s }, note)}
        />
      ) : null}

      {step.kind === 'remove' ? (
        <button
          className="primary big-action"
          disabled={busy || step.fittedSerial === null}
          onClick={() => onAnswer({ kind: 'remove', childSerial: step.fittedSerial ?? '' }, note)}
        >
          {step.fittedSerial ? `Take out ${step.fittedSerial}` : 'Nothing is fitted there'}
        </button>
      ) : null}

      {step.kind === 'consume' ? (
        <>
          <label className="field">
            <span className="field-label">{step.itemTypeName} — the step calls for {step.amount}</span>
            <input type="number" inputMode="numeric" className="big-input" value={amount} onChange={(ev) => setAmount(ev.target.value)} />
          </label>
          <button className="primary big-action" disabled={busy || Number(amount) <= 0} onClick={() => onAnswer({ kind: 'consume', amount: Number(amount) }, note)}>
            Use {amount} from the shelf
          </button>
        </>
      ) : null}

      {step.kind === 'note' ? (
        <button className="primary big-action" disabled={busy} onClick={() => onAnswer({ kind: 'note' }, note)}>
          Done
        </button>
      ) : null}

      {step.answer ? (
        <p className="muted step-card__answered">
          Answered {new Date(step.answer.answeredAt).toLocaleString()}
          {step.answer.note ? ` — ${step.answer.note}` : ''}. Answering again replaces it until the job is closed.
        </p>
      ) : null}
    </div>
  )
}

function band(step: StepView, value: number): boolean | null {
  if (step.lower === null && step.upper === null) return null
  if (step.lower !== null && value < step.lower) return false
  if (step.upper !== null && value > step.upper) return false
  return true
}

/** A failed check, or a measurement outside its band. */
export function outOfBand(s: StepView): boolean {
  if (s.answer?.kind === 'check') return s.answer.checkResult === 'fail'
  if (s.answer?.kind !== 'measure' || s.answer.value === null) return false
  if (s.lower !== null && s.answer.value < s.lower) return true
  return s.upper !== null && s.answer.value > s.upper
}
